import {I18n} from '@aws-amplify/core';

import {SimplePageHeader} from '@flexinet/ui-components';

import {useVerifiedPermissions} from '@flexinet/auth-components';
import {rbacActions} from '../../../config/rbac/actions';
import {ReloadRecordsButton} from '../buttons/ReloadRecordsButton';
import {DeleteRecordButton} from '../buttons/DeleteRecordButton';
import {AddRecordButton} from '../buttons/AddRecordButton';

/**
 * The index toolbar, composed in one place so what this module offers is
 * readable at a glance (§2).
 *
 * Order is [Reload] then Delete/Add — the same shape as the customers, invoices
 * and prices modules next door, minus Filter.
 *
 * ⚠️ THERE IS NO FILTER BUTTON, and that is deliberate rather than forgotten —
 * §2 requires a module to state this rather than leave the absence
 * unexplained. `listProducts` returns exactly two fields, `id` and `name`, both
 * free text. A filter drawer over them would either duplicate the keyword
 * search or present an empty select, so there is nothing an operator could
 * meaningfully pick from. See useProductsFilter.ts, which keeps the store as
 * the seam a real filter attaches to the day the API returns a filterable
 * field.
 *
 * Every button carries a stable `key`: `tools` is an ARRAY of elements, which
 * is the toolbar contract — not a hand-built <Group> (§2).
 *
 * Buttons are HIDDEN, not disabled, when RBAC forbids the action (§10).
 */
const useTools = (selectedIds: string[], onDeleteComplete?: () => void) => {

    const {allowedActions} = useVerifiedPermissions();

    const tools = [<ReloadRecordsButton key='reload'/>];

    if (allowedActions.includes(rbacActions.DeleteProduct)) {
        tools.push(
            <DeleteRecordButton
                key='delete'
                selectedIds={selectedIds}
                onDeleteComplete={onDeleteComplete}
            />,
        );
    }

    if (allowedActions.includes(rbacActions.CreateProduct)) {
        tools.push(<AddRecordButton key='add'/>);
    }

    return tools;
};

interface IndexPageHeaderProps {
    selectedIds?: string[];
    onDeleteComplete?: () => void;
}

export const IndexPageHeader = ({
                                    selectedIds = [],
                                    onDeleteComplete,
                                }: IndexPageHeaderProps) => {
    return (
        <SimplePageHeader
            title={I18n.get('Products')}
            tools={useTools(selectedIds, onDeleteComplete)}
        />
    );
};
